import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { formatDate } from "../lib/date";
import { toast } from "sonner";
import {
  Building2,
  ClipboardList,
  LogOut,
  RefreshCw,
  Clock,
  KeyRound,
  Mail,
  Maximize,
  Minimize,
  Loader2,
  CheckCircle,
  AlertCircle,
  XCircle,
  Timer,
  Calendar,
  History,
  ShieldCheck,
  Truck,
  Lock,
  ChevronLeft,
  ChevronRight,
  Tv
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auto-refresh interval in milliseconds (5 minutes)
const AUTO_REFRESH_INTERVAL = 5 * 60 * 1000;

export default function ClientDashboards() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Auth state
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enteredClientId, setEnteredClientId] = useState(clientId || "");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [quickLoginValidated, setQuickLoginValidated] = useState(false);
  
  // Dashboard state
  const [availableDashboards, setAvailableDashboards] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [companyName, setCompanyName] = useState("");
  
  // Jobs Board state
  const [jobsData, setJobsData] = useState(null);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTvMode, setIsTvMode] = useState(false); // TV mode = fullscreen + compact
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  // Auto-select date logic:
  // - After 3pm on weekdays: show tomorrow
  // - After 3pm on Friday: show Saturday & Sunday (weekend mode)
  // - On Saturday or Sunday: show both Saturday & Sunday (weekend mode)
  const getInitialDate = () => {
    const now = new Date();
    const hour = now.getHours();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 5 = Friday, 6 = Saturday
    
    if (hour >= 15) {
      // After 3pm, show tomorrow's jobs
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow;
    }
    return now;
  };
  
  const [selectedDate, setSelectedDate] = useState(getInitialDate);
  
  // Weekend mode: show both Saturday AND Sunday jobs
  // Triggered by: Friday after 3pm, or any time on Saturday/Sunday
  const isWeekendMode = (() => {
    const now = new Date();
    const hour = now.getHours();
    const todayDayOfWeek = now.getDay(); // 0 = Sunday, 5 = Friday, 6 = Saturday
    const selectedDayOfWeek = selectedDate.getDay();
    
    // If it's Friday after 3pm and showing Saturday
    if (todayDayOfWeek === 5 && hour >= 15 && selectedDayOfWeek === 6) {
      return true;
    }
    // If today is Saturday or Sunday and selected date is Saturday or Sunday
    if ((todayDayOfWeek === 0 || todayDayOfWeek === 6) && 
        (selectedDayOfWeek === 0 || selectedDayOfWeek === 6)) {
      return true;
    }
    // If user manually selected Saturday, also show Sunday
    if (selectedDayOfWeek === 6) {
      return true;
    }
    return false;
  })();
  
  // Compact mode for TV displays - via URL param OR TV mode button
  const isCompactMode = searchParams.get('compact') === 'true' || isTvMode;
  
  // Auto-refresh
  const refreshTimerRef = useRef(null);

  // Set document title
  useEffect(() => {
    document.title = companyName ? `${companyName} | Dashboard` : "EQUIPR | Client Dashboard";
    return () => {
      document.title = "EQUIPR | Intelligence";
    };
  }, [companyName]);

  // Check for quick login token in URL and auto-authenticate
  useEffect(() => {
    const token = searchParams.get('token');
    if (token && !quickLoginValidated && !session) {
      authenticateWithQuickLogin(token);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, session]);

  const authenticateWithQuickLogin = async (token) => {
    setLoading(true);
    try {
      // Directly authenticate with the token (no OTP required)
      const response = await axios.post(`${API}/client-dashboards/quick-login/authenticate/${token}`);
      
      if (response.data.success) {
        const sessionData = response.data.session;
        setSession(sessionData);
        setEnteredClientId(sessionData.client_id);
        setEmail(sessionData.email || "");
        setCompanyName(sessionData.company_name || "");
        setQuickLoginValidated(true);
        localStorage.setItem(`dashboard_session_${sessionData.client_id}`, JSON.stringify(sessionData));
        toast.success(`Welcome to ${sessionData.company_name} Dashboard`);
      }
    } catch (error) {
      // If direct auth fails, fall back to validation only (pre-fill mode)
      try {
        const validateResponse = await axios.get(`${API}/client-dashboards/quick-login/validate/${token}`);
        if (validateResponse.data.valid) {
          setEnteredClientId(validateResponse.data.client_id);
          setEmail(validateResponse.data.email || "");
          setCompanyName(validateResponse.data.company_name || "");
          setQuickLoginValidated(true);
          toast.info(`Please verify your email for ${validateResponse.data.company_name}`);
        }
      } catch (validateError) {
        toast.error(error.response?.data?.detail || "Invalid or expired link");
      }
    } finally {
      setLoading(false);
    }
  };

  // Check for existing session on mount
  useEffect(() => {
    const activeClientId = clientId || enteredClientId;
    if (!activeClientId) return;
    
    const savedSession = localStorage.getItem(`dashboard_session_${activeClientId.toUpperCase()}`);
    if (savedSession) {
      try {
        const parsed = JSON.parse(savedSession);
        // Check if session is still valid
        if (new Date(parsed.expires_at) > new Date()) {
          setSession(parsed);
          setCompanyName(parsed.company_name || "");
        } else {
          localStorage.removeItem(`dashboard_session_${activeClientId.toUpperCase()}`);
        }
      } catch (e) {
        localStorage.removeItem(`dashboard_session_${activeClientId.toUpperCase()}`);
      }
    }
  }, [clientId, enteredClientId]);

  // Fetch available dashboards when session is active
  useEffect(() => {
    if (session) {
      fetchAvailableDashboards();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Auto-refresh for Jobs Board
  useEffect(() => {
    if (selectedDashboard === "jobs-board" && session) {
      // Initial fetch
      fetchJobsBoard();
      
      // Set up auto-refresh
      refreshTimerRef.current = setInterval(() => {
        fetchJobsBoard();
      }, AUTO_REFRESH_INTERVAL);
      
      return () => {
        if (refreshTimerRef.current) {
          clearInterval(refreshTimerRef.current);
        }
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDashboard, session, selectedDate]);  // Re-fetch when date changes

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);
      // If exiting fullscreen (e.g., via Escape key), also exit TV mode
      if (!isNowFullscreen && isTvMode) {
        setIsTvMode(false);
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, [isTvMode]);

  const fetchAvailableDashboards = async () => {
    try {
      const activeClientId = session?.client_id || enteredClientId.toUpperCase();
      const response = await axios.get(`${API}/client-dashboards/${activeClientId}/available`);
      setAvailableDashboards(response.data.dashboards || []);
      setCompanyName(response.data.company_name || "");
    } catch (error) {
      console.error("Failed to fetch dashboards:", error);
      toast.error("Failed to load dashboards");
    }
  };

  // Format a JS Date as "YYYY-MM-DD" using its **local** components.
  // (Using `toISOString().split('T')[0]` returns the UTC date, which silently
  // drifts the queried day by ±1 for users east of UTC — Riverina would see
  // the header roll to tomorrow while the grid still showed today.)
  const toLocalDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const fetchJobsBoard = async () => {
    setLoadingJobs(true);
    try {
      const activeClientId = session?.client_id || enteredClientId.toUpperCase();
      
      if (isWeekendMode) {
        // Weekend mode: fetch both Saturday and Sunday
        const saturday = new Date(selectedDate);
        // If selected is Sunday, go back to Saturday
        if (saturday.getDay() === 0) {
          saturday.setDate(saturday.getDate() - 1);
        }
        const sunday = new Date(saturday);
        sunday.setDate(sunday.getDate() + 1);
        
        const satDateStr = toLocalDateStr(saturday);
        const sunDateStr = toLocalDateStr(sunday);
        
        // Fetch both days in parallel
        const [satResponse, sunResponse] = await Promise.all([
          axios.get(`${API}/client-dashboards/${activeClientId}/jobs-board?date=${satDateStr}`),
          axios.get(`${API}/client-dashboards/${activeClientId}/jobs-board?date=${sunDateStr}`)
        ]);
        
        // Combine the rows from both days
        const combinedRows = [
          ...(satResponse.data.rows || []),
          ...(sunResponse.data.rows || [])
        ];
        
        // Use columns from the first response (they should be the same)
        setJobsData({
          ...satResponse.data,
          rows: combinedRows,
          row_count: combinedRows.length,
          weekend_mode: true,
          dates: [satDateStr, sunDateStr]
        });
        setCompanyName(satResponse.data.company_name || companyName);
      } else {
        // Normal single day fetch
        const dateStr = toLocalDateStr(selectedDate);
        const response = await axios.get(`${API}/client-dashboards/${activeClientId}/jobs-board?date=${dateStr}`);
        setJobsData({...response.data, weekend_mode: false});
        setCompanyName(response.data.company_name || companyName);
      }
      
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch jobs board:", error);
      toast.error(error.response?.data?.detail || "Failed to load jobs data");
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
      setDatePickerOpen(false);
    }
  };

  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  // Format date display - shows "Sat & Sun" in weekend mode
  const getDateDisplayText = (short = false) => {
    if (isWeekendMode) {
      const saturday = new Date(selectedDate);
      if (saturday.getDay() === 0) {
        saturday.setDate(saturday.getDate() - 1);
      }
      const sunday = new Date(saturday);
      sunday.setDate(sunday.getDate() + 1);
      
      if (short) {
        return `Sat ${saturday.getDate()} & Sun ${sunday.getDate()} ${saturday.toLocaleDateString("en-AU", { month: "short" })}`;
      }
      return `Sat ${saturday.getDate()} & Sun ${sunday.getDate()} ${saturday.toLocaleDateString("en-AU", { month: "short", year: "numeric" })}`;
    }
    
    if (short) {
      return selectedDate.toLocaleDateString("en-AU", { 
        weekday: "short", 
        day: "numeric",
        month: "short"
      });
    }
    return selectedDate.toLocaleDateString("en-AU", { 
      weekday: "short", 
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const handleRequestOTP = async () => {
    if (!enteredClientId) {
      toast.error("Please enter your Client ID");
      return;
    }
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/client-dashboards/generate-otp`, {
        client_id: enteredClientId.toUpperCase(),
        email: email
      });
      
      setOtpSent(true);
      setGeneratedOtp(response.data.otp || ""); // For dev/demo purposes
      toast.success("Access code sent to your email");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send access code");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) {
      toast.error("Please enter the access code");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/client-dashboards/verify-otp`, {
        client_id: enteredClientId.toUpperCase(),
        email: email,
        otp: otp
      });
      
      const sessionData = response.data.session;
      setSession(sessionData);
      setCompanyName(sessionData.company_name || "");
      localStorage.setItem(`dashboard_session_${enteredClientId.toUpperCase()}`, JSON.stringify(sessionData));
      toast.success("Welcome to your dashboard!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid access code");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setSession(null);
    setSelectedDashboard(null);
    setJobsData(null);
    setEnteredClientId(clientId || "");
    setEmail("");
    setOtp("");
    setOtpSent(false);
    localStorage.removeItem(`dashboard_session_${enteredClientId.toUpperCase()}`);
    toast.success("Logged out successfully");
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // TV Mode - fullscreen + compact for TV displays
  const toggleTvMode = () => {
    if (!isTvMode) {
      // Enter TV mode
      setIsTvMode(true);
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
      }
    } else {
      // Exit TV mode
      setIsTvMode(false);
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    }
  };

  // Login View
  if (!session) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[#111111] border-[#27272A]">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-[#007AFF]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-[#007AFF]" />
            </div>
            <CardTitle className="text-2xl text-white">Client Dashboard</CardTitle>
            <p className="text-[#A1A1AA] text-sm mt-2">
              Enter your credentials to access your dashboard
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {!otpSent ? (
              <>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Client ID</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                    <Input
                      type="text"
                      placeholder="e.g. RCS265"
                      value={enteredClientId}
                      onChange={(e) => setEnteredClientId(e.target.value.toUpperCase())}
                      className="pl-10 bg-[#1A1A1A] border-[#27272A] text-white uppercase"
                      data-testid="dashboard-client-id-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleRequestOTP()}
                      className="pl-10 bg-[#1A1A1A] border-[#27272A] text-white"
                      data-testid="dashboard-email-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleRequestOTP}
                  disabled={loading || !enteredClientId || !email}
                  className="w-full bg-[#007AFF] hover:bg-[#007AFF]/90 text-white"
                  data-testid="dashboard-request-otp-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <KeyRound className="w-4 h-4 mr-2" />
                  )}
                  Request Access Code
                </Button>
              </>
            ) : (
              <>
                <div className="text-center mb-4">
                  <p className="text-[#A1A1AA] text-sm">
                    We sent a code to <span className="text-white">{email}</span>
                  </p>
                  {generatedOtp && (
                    <p className="text-[#30D158] text-sm mt-2">
                      Demo Code: <span className="font-mono font-bold">{generatedOtp}</span>
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Access Code</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                    <Input
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleVerifyOTP()}
                      className="pl-10 bg-[#1A1A1A] border-[#27272A] text-white text-center text-xl tracking-widest"
                      maxLength={6}
                      data-testid="dashboard-otp-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleVerifyOTP}
                  disabled={loading || otp.length !== 6}
                  className="w-full bg-[#30D158] hover:bg-[#30D158]/90 text-white"
                  data-testid="dashboard-verify-otp-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Verify & Access Dashboard
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setOtpSent(false);
                    setOtp("");
                    setGeneratedOtp("");
                  }}
                  className="w-full text-[#A1A1AA] hover:text-white"
                >
                  Change credentials
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard Selection View
  if (!selectedDashboard) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#007AFF]/20 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#007AFF]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{companyName || clientId}</h1>
              <p className="text-[#A1A1AA] text-sm">Select a dashboard to view</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="border-[#27272A] text-[#A1A1AA] hover:text-white hover:bg-[#1A1A1A]"
            data-testid="dashboard-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {availableDashboards.map((dashboard) => {
            // Get the appropriate icon based on dashboard type
            const IconComponent = {
              "clipboard-list": ClipboardList,
              "history": History,
              "shield-check": ShieldCheck,
              "truck": Truck
            }[dashboard.icon] || ClipboardList;
            
            const isComingSoon = dashboard.coming_soon;
            
            return (
              <Card
                key={dashboard.id}
                className={`bg-[#111111] border-[#27272A] transition-colors ${
                  isComingSoon 
                    ? "opacity-60 cursor-not-allowed" 
                    : "hover:border-[#007AFF]/50 cursor-pointer"
                }`}
                onClick={() => !isComingSoon && setSelectedDashboard(dashboard.id)}
                data-testid={`dashboard-card-${dashboard.id}`}
              >
                <CardContent className="p-6 relative">
                  {isComingSoon && (
                    <Badge className="absolute top-4 right-4 bg-[#27272A] text-[#A1A1AA] text-xs">
                      <Lock className="w-3 h-3 mr-1" />
                      Coming Soon
                    </Badge>
                  )}
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${dashboard.color}20` }}
                  >
                    <IconComponent className="w-7 h-7" style={{ color: dashboard.color }} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{dashboard.name}</h3>
                  <p className="text-[#A1A1AA] text-sm">{dashboard.description}</p>
                </CardContent>
              </Card>
            );
          })}

          {availableDashboards.length === 0 && (
            <div className="col-span-full text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[#A1A1AA] opacity-50" />
              <p className="text-[#A1A1AA]">No dashboards available</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Jobs Board View
  if (selectedDashboard === "jobs-board") {
    return (
      <div className={`min-h-screen bg-[#0A0A0A] ${isFullscreen ? (isCompactMode ? "p-2" : "p-4") : "p-6"}`}>
        {/* Header - Hidden in fullscreen, show minimal info bar instead */}
        {!isFullscreen ? (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => setSelectedDashboard(null)}
                className="text-[#A1A1AA] hover:text-white p-2"
                data-testid="jobs-board-back-btn"
              >
                <XCircle className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-white text-2xl">
                  {companyName || clientId} - Jobs Board
                </h1>
                <div className="flex items-center gap-4 text-[#A1A1AA] text-sm mt-1">
                  {/* Date Selector */}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={goToPreviousDay}
                      className="p-1 h-auto text-[#A1A1AA] hover:text-white hover:bg-[#27272A]"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    
                    <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          className={`flex items-center gap-2 text-sm px-2 py-1 text-white hover:bg-[#27272A] border border-[#27272A] rounded-md ${isWeekendMode ? "bg-[#FF9F0A]/10 border-[#FF9F0A]/30" : ""}`}
                        >
                          <Calendar className="w-4 h-4" />
                          {getDateDisplayText()}
                          {isWeekendMode && <span className="text-[#FF9F0A] text-xs ml-1">Weekend</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#111111] border-[#27272A]" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={selectedDate}
                          onSelect={handleDateChange}
                          initialFocus
                          className="bg-[#111111] text-white"
                        />
                        <div className="p-2 border-t border-[#27272A]">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={goToToday}
                            className="w-full border-[#27272A] text-white hover:bg-[#27272A]"
                          >
                            Today
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={goToNextDay}
                      className="p-1 h-auto text-[#A1A1AA] hover:text-white hover:bg-[#27272A]"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>

                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date().toLocaleTimeString("en-AU", { 
                      hour: "2-digit", 
                      minute: "2-digit",
                      hour12: false
                    })}
                  </span>
                  {lastUpdated && (
                    <span className="text-[#71717A]">
                      Updated: {lastUpdated.toLocaleTimeString("en-AU", { 
                        hour: "2-digit", 
                        minute: "2-digit",
                        hour12: false
                      })}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={fetchJobsBoard}
                disabled={loadingJobs}
                className="border-[#27272A] text-[#A1A1AA] hover:text-white hover:bg-[#1A1A1A]"
                data-testid="jobs-board-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingJobs ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                onClick={toggleFullscreen}
                className="border-[#27272A] text-[#A1A1AA] hover:text-white hover:bg-[#1A1A1A]"
                data-testid="jobs-board-fullscreen-btn"
                title="Fullscreen"
              >
                <Maximize className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                onClick={toggleTvMode}
                className="border-[#27272A] text-[#A1A1AA] hover:text-white hover:bg-[#1A1A1A]"
                data-testid="jobs-board-tv-btn"
                title="TV Display Mode (fullscreen + compact)"
              >
                <Tv className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-[#27272A] text-[#A1A1AA] hover:text-white hover:bg-[#1A1A1A]"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          /* Minimal fullscreen header - just essential info */
          <div className={`flex items-center justify-between ${isCompactMode ? "mb-1 text-sm" : "mb-2 text-base"} text-[#A1A1AA]`}>
            <div className="flex items-center gap-3">
              <span className="font-semibold text-white">{companyName || clientId}</span>
              <span>•</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={goToPreviousDay}
                  className="p-0.5 h-auto text-[#A1A1AA] hover:text-white"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className={`font-medium ${isWeekendMode ? "text-[#FF9F0A]" : "text-white"}`}>
                  {getDateDisplayText(true)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={goToNextDay}
                  className="p-0.5 h-auto text-[#A1A1AA] hover:text-white"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <span>•</span>
              <span>{jobsData?.row_count || 0} jobs</span>
              {isTvMode && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1 text-[#30D158]">
                    <Tv className="w-4 h-4" />
                    TV
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {new Date().toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false })}
              </span>
              {lastUpdated && (
                <span className="text-[#71717A]">
                  Updated: {lastUpdated.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false })}
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchJobsBoard}
                disabled={loadingJobs}
                className="p-1 h-auto text-[#A1A1AA] hover:text-white"
              >
                <RefreshCw className={`w-4 h-4 ${loadingJobs ? "animate-spin" : ""}`} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={isTvMode ? toggleTvMode : toggleFullscreen}
                className="p-1 h-auto text-[#A1A1AA] hover:text-white"
                title={isTvMode ? "Exit TV Mode" : "Exit Fullscreen"}
              >
                <Minimize className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Jobs Table */}
        {loadingJobs && !jobsData ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
          </div>
        ) : jobsData?.rows?.length > 0 ? (
          <div className="overflow-auto bg-[#111111] border border-[#27272A] rounded-lg">
            {/* Column name mapping for display */}
            {(() => {
              const columnDisplayNames = {
                'BookingId': 'ID',
                'Bookingid': 'ID',
                'bookingid': 'ID',
                'BookingDate': 'DATE',
                'Bookingdate': 'DATE',
                'bookingdate': 'DATE',
                'BookingTime': 'TIME',
                'Bookingtime': 'TIME',
                'bookingtime': 'TIME',
                'WorkDescription': 'WORK DESCRIPTION',
                'Workdescription': 'WORK DESCRIPTION',
                'workdescription': 'WORK DESCRIPTION',
                'OtherWorkers': 'CREW',
                'Otherworkers': 'CREW',
                'otherworkers': 'CREW',
                'AssetId': 'ASSET ID',
                'Asset Type': 'ASSET TYPE',
                'Status': 'STATUS'
              };
              
              const getDisplayName = (col) => columnDisplayNames[col] || col.toUpperCase();
              
              // Status pill configuration
              const getStatusStyle = (status) => {
                const statusLower = (status || '').toLowerCase();
                
                if (statusLower.includes('to be confirmed') || statusLower === 'to be confirmed') {
                  return { bg: 'bg-[#6B7280]', text: 'text-white', icon: null };
                }
                if (statusLower.includes('no supplier') || statusLower === 'no supplier') {
                  return { bg: 'bg-[#FF453A]', text: 'text-white', icon: null };
                }
                if (statusLower.includes('invoice is ready') || statusLower === 'invoice is ready') {
                  return { bg: 'bg-[#FFD60A]', text: 'text-black', icon: 'check-green' };
                }
                if (statusLower.includes('invoiced') || statusLower === 'invoiced') {
                  return { bg: 'bg-[#FFD60A]', text: 'text-black', icon: null };
                }
                if (statusLower.includes('docket submitted') || statusLower === 'docket submitted') {
                  return { bg: 'bg-[#007AFF]', text: 'text-white', icon: null };
                }
                if (statusLower.includes('work completed') || statusLower === 'work completed') {
                  return { bg: 'bg-transparent', text: 'text-[#007AFF]', icon: 'check-blue' };
                }
                if (statusLower.includes('booking confirmed') || statusLower === 'booking confirmed') {
                  return { bg: 'bg-transparent', text: 'text-[#30D158]', icon: 'check-green' };
                }
                if (statusLower.includes('booking cancelled') || statusLower === 'booking cancelled' || statusLower.includes('cancelled')) {
                  return { bg: 'bg-[#FF453A]/20', text: 'text-[#FF453A]', icon: null, cancelled: true };
                }
                // Default
                return { bg: 'bg-[#27272A]', text: 'text-[#A1A1AA]', icon: null };
              };
              
              // Columns to exclude from display
              const excludedColumns = ['addedBy', 'AddedBy', 'Added By', 'updatedBy', 'UpdatedBy', 'Updated By'];
              
              // Get display columns - Status first, then others (excluding hidden ones)
              const hasStatusColumn = jobsData.columns.some(col => col.toLowerCase() === 'status');
              const otherColumns = jobsData.columns.filter(col => 
                !excludedColumns.includes(col) && col.toLowerCase() !== 'status'
              );
              const displayColumns = hasStatusColumn 
                ? ['Status', ...otherColumns]
                : otherColumns;
              
              return (
            <table className="w-full">
              <thead className="bg-[#1A1A1A] sticky top-0">
                <tr>
                  {displayColumns.map((col, idx) => (
                    <th
                      key={idx}
                      className={`px-4 ${isFullscreen ? (isCompactMode ? "py-2 text-sm" : "py-3 text-base") : "py-3 text-xs"} text-left font-semibold text-[#A1A1AA] uppercase tracking-wider border-b border-[#27272A]`}
                    >
                      {getDisplayName(col)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A]">
                {jobsData.rows.map((row, rowIdx) => {
                  // Extract Added By and Updated By for pills
                  const addedBy = row['addedBy'] || row['AddedBy'] || row['Added By'];
                  const updatedBy = row['updatedBy'] || row['UpdatedBy'] || row['Updated By'];
                  
                  // Get status and check if cancelled
                  const status = row['Status'] || row['status'] || '';
                  const statusStyle = getStatusStyle(status);
                  const isCancelled = statusStyle.cancelled;
                  
                  return (
                    <tr 
                      key={rowIdx} 
                      className={`${rowIdx % 2 === 0 ? "bg-[#111111]" : "bg-[#0A0A0A]"} hover:bg-[#1A1A1A] transition-colors`}
                    >
                      {displayColumns.map((col, colIdx) => {
                        const originalCol = col === 'Status' ? (jobsData.columns.find(c => c.toLowerCase() === 'status') || 'Status') : col;
                        let displayValue = row[originalCol] ?? row[col] ?? "-";
                        
                        // Format Booking Date (dd MMM YYYY)
                        if ((col.toLowerCase().includes('date') || col === 'BookingDate') && displayValue && displayValue !== "-") {
                          try {
                            const date = new Date(displayValue);
                            if (!isNaN(date.getTime())) {
                              displayValue = formatDate(date);
                            }
                          } catch (e) {}
                        }
                        
                        // Format Booking Time (24-hour HH:mm)
                        if ((col.toLowerCase().includes('time') || col === 'BookingTime') && displayValue && displayValue !== "-") {
                          try {
                            // Handle time strings like "1900-01-01T06:00:00" or "06:00:00"
                            let timeStr = displayValue;
                            if (timeStr.includes('T')) {
                              timeStr = timeStr.split('T')[1];
                            }
                            if (timeStr.includes(':')) {
                              const [hours, minutes] = timeStr.split(':');
                              displayValue = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
                            }
                          } catch (e) {}
                        }
                        
                        // Render Status column as pill
                        if (col.toLowerCase() === 'status') {
                          return (
                            <td
                              key={colIdx}
                              className={`px-4 ${isFullscreen ? (isCompactMode ? "py-2" : "py-3") : "py-4"}`}
                            >
                              <span className={`inline-flex items-center gap-1.5 ${isFullscreen ? (isCompactMode ? "px-2 py-1 text-sm" : "px-3 py-1.5 text-base") : "px-3 py-1.5 text-sm"} ${statusStyle.bg} ${statusStyle.text} rounded-full font-medium whitespace-nowrap`}>
                                {statusStyle.icon === 'check-green' && (
                                  <CheckCircle className={`${isFullscreen ? (isCompactMode ? "w-4 h-4" : "w-5 h-5") : "w-4 h-4"} text-[#30D158]`} />
                                )}
                                {statusStyle.icon === 'check-blue' && (
                                  <CheckCircle className={`${isFullscreen ? (isCompactMode ? "w-4 h-4" : "w-5 h-5") : "w-4 h-4"} text-[#007AFF]`} />
                                )}
                                {statusStyle.icon !== 'check-green' && statusStyle.icon !== 'check-blue' && displayValue}
                                {statusStyle.icon === 'check-green' && statusStyle.bg !== 'bg-transparent' && displayValue}
                              </span>
                            </td>
                          );
                        }
                        
                        return (
                          <td
                            key={colIdx}
                            className={`px-4 ${isFullscreen ? (isCompactMode ? "py-2 text-base" : "py-3 text-lg") : "py-4 text-base"} text-white whitespace-nowrap ${isCancelled ? "line-through opacity-50" : ""}`}
                          >
                            {/* Main cell content - with inline pills for Booking ID */}
                            {col.toLowerCase() === 'bookingid' && (addedBy || updatedBy) ? (
                              <div className="flex items-center gap-2">
                                <span>{displayValue}</span>
                                {addedBy && (
                                  <span className={`inline-flex items-center gap-1 ${isFullscreen ? (isCompactMode ? "px-1.5 py-0.5 text-xs" : "px-2 py-0.5 text-xs") : "px-1.5 py-0.5 text-xs"} bg-[#007AFF]/20 text-[#007AFF] rounded-full`}>
                                    <span className="opacity-70">+</span>{addedBy}
                                  </span>
                                )}
                                {updatedBy && (
                                  <span className={`inline-flex items-center gap-1 ${isFullscreen ? (isCompactMode ? "px-1.5 py-0.5 text-xs" : "px-2 py-0.5 text-xs") : "px-1.5 py-0.5 text-xs"} bg-[#30D158]/20 text-[#30D158] rounded-full`}>
                                    <span className="opacity-70">✎</span>{updatedBy}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div>{displayValue}</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
              );
            })()}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-[#A1A1AA]">
            <ClipboardList className={`${isFullscreen ? "w-16 h-16" : "w-12 h-12"} mb-4 opacity-50`} />
            <p className={isFullscreen ? "text-xl" : ""}>No jobs data available</p>
            {jobsData?.error && (
              <p className={`text-[#FF453A] ${isFullscreen ? "text-base" : "text-sm"} mt-2`}>{jobsData.error}</p>
            )}
          </div>
        )}

        {/* Stats Footer - hide in fullscreen as info is in minimal header */}
        {jobsData?.row_count > 0 && !isFullscreen && (
          <div className="mt-4 flex items-center justify-between text-sm text-[#A1A1AA]">
            <span>{jobsData.row_count} jobs</span>
            <span>Auto-refreshes every 5 minutes</span>
          </div>
        )}
      </div>
    );
  }

  return null;
}
